import { useMemo, useEffect, useState, Fragment } from 'react'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import { ApiErrorTypes } from 'ogram-react'
import { toast } from 'react-toastify'
import { Image } from 'antd'
import moment from 'moment'

import { useStore, useQuery, useMutation, useDelayedQuery } from '../../../utils/mst-hooks'
import { CherryPickingApplicant, CherryPickingApplicantOfPermanentJob } from '../../../models/cherry-picking-applicants'
import { Button } from '../../../components/button/button'
import { Separator } from '../../../components/app-layout/separator/separator'
import { IconButton } from '../../../components/icon-button/icon-button'
import Rating from '../../../components/rating/rating'
import LoadingSpinner from '../../../components/loading-spinner/loading-spinner'
import beaconImg from '../../../components/assets/beacon.svg'
import Spacer from '../../order/components/spacer/spacer'
import arrowLeftImg from './assets/arrow-left.svg'
import arrowRightImg from './assets/arrow-right.svg'
import acceptedImg from './assets/accepted.svg'
import ApplicantInfo from '../job-cherry-picking/components/applicant-info/applicant-info'
import SeniorityLevel from '../job-cherry-picking/components/seniority-level/seniority-level'
import TopQuality from '../job-cherry-picking/components/top-quality/top-quality'
import { WorkExperienceCard } from './components/work-exp-card/work-exp-card'
import { EducationCard } from './components/education-card/education-card'
import { Header } from '../../../components/app-layout'
import { withErrorBoundary } from '../../../components/error/with-error-boundary'
import { SkillsList } from '../components/skills-list/skills-list'
import { InterviewSection } from '../components/interview-section/interview-section'
import { WorkCategorySkills } from './components/work-category-skills/work-category-skills'
import { ChiefCard } from './components/chief-card/chief-card'
import { ToggleFavourite } from '../components/toggle-favourite/toggle-favourite'
import './applicant-details.scss'
import { ClientExpCard } from './components/client-exp-card/client-exp-card'
import { useAcceptApplicant } from '../../../utils/use-accept-applicant'
import { JobScheduleType } from '../../../models/job'
import { convertGoogleDriveUrlToPreview, convertGoogleDriveUrlToThumbnail } from '../../../utils/google-drive-helper'

type Props = {
  jobId: string
  jobType: JobScheduleType
  applicantId?: number // should be used only when "showControls" is false
  shiftId?: number
  showHeader: boolean
  showControls: boolean
}

const ApplicantDetails = withErrorBoundary(
  observer(({ jobId, jobType, applicantId: initialApplicantId, shiftId, showHeader, showControls }: Props) => {
    const {
      getNotAcceptedApplicantsList,
      getSPExperience,
      getExperienceBySpId,
      getApplicant,
      getInterview,
      getCurrentApplicant,
      setCurrentApplicant,
      skillsGroupedByCategory,
    } = useStore().data
    const parsedJobId = parseInt(jobId)
    const notAcceptedApplicantsList = getNotAcceptedApplicantsList(jobType)
    const currentApplicant = getCurrentApplicant(jobType)
    const applicantId = String(initialApplicantId ?? currentApplicant?.id)
    const isPermanentJob = jobType === JobScheduleType.permanent

    const {
      enableQuery: loadApplicant,
      query: { isLoading: applicantLoading },
    } = useDelayedQuery(() => getApplicant(parsedJobId, Number(applicantId)))
    const { isLoading } = useQuery(
      () => {
        if (isPermanentJob) {
          return Promise.resolve()
        }
        return getInterview({ jobId: parsedJobId })
      },
      {
        filterError: [ApiErrorTypes.NotFound],
      },
    )

    useEffect(() => {
      if (!isPermanentJob && !isNaN(parseInt(applicantId))) {
        loadApplicant()
      }
    }, [jobId, applicantId, jobType])

    const experienceList = getExperienceBySpId(applicantId)
    const applicant = currentApplicant
    const [rating, setRating] = useState((applicant as CherryPickingApplicant | undefined)?.rating ?? 0)

    const [, getExperience] = useMutation(getSPExperience)
    const { acceptApplicant, rejectApplicant, acceptQuery, rejectQuery } = useAcceptApplicant({
      isPermanentJob,
      onAccepted: () => {
        toast.success('Ogrammer is accepted!')
      },
    })

    useEffect(() => {
      if (!isPermanentJob && !isNaN(parseInt(applicantId))) {
        getExperience({ jobId, spId: applicantId })
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId, applicantId])

    const nextApplicant = useMemo(() => {
      const indexOfCurrentApplicant = notAcceptedApplicantsList.findIndex(item => item.id === Number(applicantId))
      return (
        indexOfCurrentApplicant === notAcceptedApplicantsList.length - 1
          ? notAcceptedApplicantsList[0]
          : notAcceptedApplicantsList[indexOfCurrentApplicant + 1]
      ) as CherryPickingApplicant | CherryPickingApplicantOfPermanentJob | undefined
    }, [notAcceptedApplicantsList, applicantId])

    const previousApplicant = useMemo(() => {
      const indexOfCurrentApplicant = notAcceptedApplicantsList.findIndex(item => item.id === Number(applicantId))
      return (
        indexOfCurrentApplicant === 0
          ? notAcceptedApplicantsList[notAcceptedApplicantsList.length - 1]
          : notAcceptedApplicantsList[indexOfCurrentApplicant - 1]
      ) as CherryPickingApplicant | CherryPickingApplicantOfPermanentJob | undefined
    }, [notAcceptedApplicantsList, applicantId])

    const isPhotoPDFType = useMemo(() => {
      if (isPermanentJob) return false
      return (applicant as CherryPickingApplicant | undefined)?.image_url?.includes('.pdf')
    }, [applicant])

    const changePage = (type: 'next' | 'previous') => {
      if (type === 'next' && nextApplicant) {
        setCurrentApplicant(nextApplicant.id, jobType)
      } else {
        if (previousApplicant) {
          setCurrentApplicant(previousApplicant.id, jobType)
        }
      }
    }

    const { t } = useTranslation()

    const skills = (applicant as CherryPickingApplicant | undefined)?.skills ?? []

    const onAccept = () => {
      acceptApplicant(
        jobType === JobScheduleType.permanent
          ? {
            job_application_id: applicant?.id as number,
          }
          : {
            sp_id: String(applicant?.id),
            job_id: Number(jobId),
            shift_ids: shiftId ? [shiftId] : undefined,
          },
      )
    }

    const mainExperiences = experienceList?.experiences.filter(experience => experience.is_main_experience) ?? []
    const mainWorkCategoryName = mainExperiences[0]?.work_category_name

    if (applicantLoading) {
      return (
        <div className="ApplicantDetails">
          <LoadingSpinner />
        </div>
      )
    }

    if (!applicant) return null

    return (
      <div className="ApplicantDetails">
        {(acceptQuery.isLoading || rejectQuery.isLoading) && (
          <div className="ApplicantDetails__loading">
            <LoadingSpinner />
          </div>
        )}
        {showHeader && <Header title={applicant.name ?? 'Applicant'} showBack />}
        <div className="ApplicantDetails__header">
          {!isPermanentJob && <ToggleFavourite className="ApplicantDetails__toggleFavourite" />}
          <div className="ApplicantDetails__imagesContainer">
            <div className="ApplicantDetails__avatarContainer">
              {isPhotoPDFType ? (
                <img className="ApplicantDetails__avatarContainer__avatar" />
              ) : (
                <Image
                  src={convertGoogleDriveUrlToThumbnail(applicant.image as string) as string}
                  className="ApplicantDetails__avatarContainer__avatar"
                />
              )}
              {(applicant as CherryPickingApplicant).is_chief ? (
                <img src={beaconImg} className="ApplicantDetails__avatarContainer__beacon" />
              ) : null}
            </div>
            {'fullbody_photo' in applicant &&
              applicant.fullbody_photo !== null &&
              applicant.fullbody_photo.length > 0 && (
                <Image
                  src={convertGoogleDriveUrlToThumbnail(applicant.fullbody_photo) as string}
                  className="ApplicantDetails__avatarContainer__avatar"
                />
              )}
          </div>
          <div className="ApplicantDetails__nameAndRatingContainer">
            {applicant.name !== null && <h2 className="ApplicantDetails__name">{applicant.name}</h2>}
            {'completed_shifts_count' in applicant && applicant.completed_shifts_count > 0 && (
              <p className="ApplicantDetails__shiftsCount">
                {t('applicant_details.n_shifts_completed', { count: applicant.completed_shifts_count })}
              </p>
            )}
            {!isPermanentJob && <Rating size={20} editable={false} onRatingChange={setRating} rating={rating} />}
            {'is_chief' in applicant && applicant.is_chief && (
              <div className="ApplicantDetails__nameAndRatingContainer__chiefContainer">
                <img src={beaconImg} className="ApplicantDetails__nameAndRatingContainer__chiefContainer__beacon" />
                <span className="ApplicantDetails__nameAndRatingContainer__chiefContainer__label">
                  {t('applicant_details.chief_og')}
                </span>
              </div>
            )}
          </div>
          {'top_qualities' in applicant && applicant.top_qualities?.length && (
            <div className="ApplicantDetails__topQualitiesContainer">
              {applicant.top_qualities.map((topQuality, i) => (
                <TopQuality containerStyleClass={'ApplicantDetails__topQuality'} key={i} name={topQuality} />
              ))}
            </div>
          )}
          <SkillsList skills={skills} />
          {skills.length > 0 && <Spacer space={21} />}
          {'seniority_level' in applicant && (
            <SeniorityLevel
              containerStyleClass={'ApplicantDetails__seniorityLevel'}
              seniority={applicant.seniority_level}
            />
          )}
        </div>
        {'is_chief' in applicant && applicant.is_chief && (
          <>
            <Spacer space={16} />
            <ChiefCard />
          </>
        )}
        {isPermanentJob ? (
          'video' in applicant && typeof applicant.video === 'string' ? (
            applicant.video.includes('drive.google.com') ? (
              <iframe
                src={convertGoogleDriveUrlToPreview(applicant.video) as string}
                id="applicantVideo"
                className="ApplicantDetails__videoIframe"
              ></iframe>
            ) : (
              <video key={applicant.video} controls className="AnswerForm__videoPlayer" width="100%">
                <source src={applicant.video} type="video/mp4" />
                {t('interview.video_player_not_supported')}
              </video>
            )
          ) : null
        ) : (
          <InterviewSection jobId={parsedJobId} spId={applicant.id} isLoading={isLoading} onAcceptPress={onAccept} />
        )}
        <div className="ApplicantDetails__informationContainer">
          <h3 className="ApplicantDetails__infoTitle">{t('applicant_details.information')}</h3>
          <ApplicantInfo
            height={'height' in applicant ? applicant.height : null}
            languages={applicant.languages}
            years_experience={'years_experience' in applicant ? applicant.years_experience : null}
            age={applicant.age}
            nationality={applicant.nationality}
            gender={'gender' in applicant ? applicant.gender : null}
            location={'location' in applicant ? applicant.location : null}
          />
        </div>
        {skills.length > 0 && (
          <>
            <div className="ApplicantDetails__infoContainer">
              <span className="ApplicantDetails__infoContainer__title">{t('applicant_details.skills')}</span>
              <Spacer space={24} />
              {skillsGroupedByCategory.map((item, key) => (
                <WorkCategorySkills key={key} categoryTitle={item.workCategoryName} skills={item.skills} />
              ))}
            </div>
            <Spacer space={25} />
          </>
        )}
        {'past_jobs' in applicant && Boolean(applicant.past_jobs.length) && (
          <div className="ApplicantDetails__infoContainer">
            <span className="ApplicantDetails__infoContainer__title">
              {t('applicant_details.job_history_with_you')}
            </span>
            <Spacer space={24} />
            {applicant.past_jobs.map((pastJob, index) => (
              <>
                <ClientExpCard
                  title={pastJob.designation_name}
                  location={pastJob.location_name}
                  jobId={pastJob.id}
                  startDate={moment(pastJob.start_date).format('D MMM')}
                  endDate={pastJob.end_date ? moment(pastJob.end_date).format('D MMM') : null}
                />
                {index !== applicant.past_jobs.length - 1 && (
                  <div className="ApplicantDetails__infoContainer__separator" />
                )}
              </>
            ))}
          </div>
        )}
        {Boolean(mainExperiences.length) && (
          <>
            <div className="ApplicantDetails__infoContainer">
              <span className="ApplicantDetails__infoContainer__title">
                {t('applicant_details.work_category_experience', { workCategoryName: mainWorkCategoryName })}
              </span>
              <Spacer space={12} />
              {mainExperiences.map((exp, index) => (
                <Fragment key={index}>
                  <WorkExperienceCard
                    title={exp.work_category_name}
                    companyName={exp.company_name}
                    location={exp.location_name}
                    startYear={exp.start_year}
                    endYear={exp.end_year}
                    description={exp.description}
                    experienceInMonths={exp.experience_months}
                    imageURL={exp.image_url}
                    duration={null}
                  />
                  {index !== mainExperiences.length - 1 && (
                    <div className="ApplicantDetails__infoContainer__separator" />
                  )}
                </Fragment>
              ))}
            </div>
            <Spacer space={25} />
          </>
        )}
        {Boolean(experienceList?.educations.length) && (
          <div className="ApplicantDetails__infoContainer">
            <span className="ApplicantDetails__infoContainer__title">{t('applicant_details.education')}</span>
            <Spacer space={12} />
            {experienceList?.educations.map((item, index) => (
              <Fragment key={index}>
                <EducationCard school={item.school} degree={item.degree} field={item.study} />
                {index !== experienceList.educations.length - 1 && (
                  <div className="ApplicantDetails__infoContainer__separator" />
                )}
              </Fragment>
            ))}
          </div>
        )}
        {'education' in applicant && applicant.education?.length && (
          <div className="ApplicantDetails__infoContainer">
            <span className="ApplicantDetails__infoContainer__title">{t('applicant_details.education')}</span>
            <Spacer space={12} />
            <span className="EducationCard__info__degreeContainer__field">{applicant.education}</span>
          </div>
        )}
        <div className="ApplicantDetails__controls-separator" />
        <Spacer space={50} />
        {showControls && (
          <>
            <div className="ApplicantDetails__controlContainer">
              <div className="ApplicantDetails__controlContainer__content">
                {
                  <>
                    <div
                      onClick={() => {
                        changePage('previous')
                      }}
                      className="ApplicantDetails__controlContainer__imgContainer"
                    >
                      <img src={arrowLeftImg} alt="get previous applicant" />
                    </div>
                    <div className="ApplicantDetails__controlContainer__buttonsContainer">
                      {applicant.isRejected ? (
                        <Button
                          title={t('job_cherry_picking.rejected')}
                          disabled
                          className="ApplicantDetails__controlContainer__buttonsContainer__rejectButton ApplicantDetails__controlContainer__buttonsContainer__rejectButton--rejected"
                        />
                      ) : !shiftId ? (
                        <>
                          <Button
                            title={t('job_cherry_picking.reject')}
                            className="ApplicantDetails__controlContainer__buttonsContainer__rejectButton"
                            onClick={() =>
                              rejectApplicant(
                                jobType === JobScheduleType.permanent
                                  ? {
                                    job_application_id: applicant.id,
                                  }
                                  : { sp_id: String(applicantId), job_id: Number(jobId) },
                              )
                            }
                          />
                        </>
                      ) : null}
                      <Separator.Horizontal width={12} />
                      {applicant.isAccepted ? (
                        <IconButton
                          title={t('job_cherry_picking.accepted')}
                          icon={acceptedImg}
                          className="ApplicantDetails__controlContainer__accepted"
                        />
                      ) : (
                        <Button
                          title={t('job_cherry_picking.accept')}
                          className="ApplicantDetails__controlContainer__buttonsContainer__acceptButton"
                          onClick={onAccept}
                        />
                      )}
                    </div>
                    <div
                      onClick={() => {
                        changePage('next')
                      }}
                      className="ApplicantDetails__controlContainer__imgContainer"
                    >
                      <img src={arrowRightImg} alt={t('applicant_details.get_next_applicant')} />
                    </div>
                  </>
                }
              </div>
            </div>
          </>
        )}
      </div>
    )
  }),
)

export default ApplicantDetails
